import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { ToasterService } from '../services/toastr/toaster.service';

const PUBLIC_URLS = [
  'Auth/',
  'Auth/forgotPassword',
  'Auth/verifyOtp',
  'Auth/resetPassword',
  'Auth/setNewPassword',
  'User/username',
  'Fingerprint/login',
];

// Local fingerprint reader agent - runs on the client PC, not our API.
// Must never receive our bearer token, and its errors must never trigger
// an auth token refresh.
const LOCAL_AGENT_URLS = [
  'localhost:5050',
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshToken$ = new BehaviorSubject<string | null>(null);

  constructor(private authService: AuthService, private _toaster: ToasterService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // 1. Local agent calls: strip ANY Authorization header, no matter who set it,
    //    and never route through refresh/toast logic.
    if (LOCAL_AGENT_URLS.some(url => req.url.includes(url))) {
      const sanitizedReq = req.headers.has('Authorization')
        ? req.clone({ headers: req.headers.delete('Authorization') })
        : req;

      return next.handle(sanitizedReq).pipe(
        catchError((error: HttpErrorResponse) => {
          // Let the calling component (e.g. addFingerPrint) show its own
          // specific message — a down local agent is an expected state.
          return throwError(() => error);
        })
      );
    }

    // 2. Public/unauthenticated backend routes.
    if (PUBLIC_URLS.some(url => req.url.includes(url))) {
      const publicReq = req.headers.has('Authorization')
        ? req.clone({ headers: req.headers.delete('Authorization') })
        : req;

      return next.handle(publicReq);
    }

    // 3. Authenticated backend routes.
    const token = this.authService.getAccessToken();

    if (token && this.authService.isTokenExpired(token)) {
      return this.handleTokenRefresh(req, next);
    }

    const authReq = token ? this.addTokenHeader(req, token) : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handleTokenRefresh(req, next);
        }
        const message = error?.error?.message ?? 'Network Error: Unable to Reach Server';
        this._toaster.showError(message);
        return throwError(() => error);
      })
    );
  }

  private handleTokenRefresh(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshToken$.next(null);

      return this.authService.refreshTokenCall().pipe(
        switchMap(response => {
          this.isRefreshing = false;
          this.refreshToken$.next(response.token);
          return next.handle(this.addTokenHeader(req, response.token));
        }),
        catchError(err => {
          this.isRefreshing = false;
          return throwError(() => err);
        })
      );
    }

    return this.refreshToken$.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next.handle(this.addTokenHeader(req, token!)))
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
  }
}